'use client';

import { useMemo, useState } from 'react';
import Link from 'next/link';
import { ChevronRight, Star, Flame, ShieldCheck, Store, Tag } from 'lucide-react';
import { buildListingHref } from '@/lib/searchRoutes';
import { ProductCard } from './ProductCard';
import { ClassifiedAdCard } from './ClassifiedAdCard';
import { ListingThumbnail } from './ListingThumbnail';
import type { ClassifiedAd } from './ClassifiedAdCard';
import { laravelPaths } from '@/lib/site';
import { ThemedExternalLink } from '@/components/ThemedExternalLink';
import type { HomePayload } from '@/lib/api';
import type { ProductSummary } from '@/lib/api';
import { useAuth } from '@/components/AuthContext';
import { ThirdPartyAd } from './ThirdPartyAd';

interface HomePageProps {
  data: HomePayload;
}

function isClassifiedAd(item: unknown): boolean {
  if (item != null && typeof item === 'object') {
    const o = item as Record<string, unknown>;
    return !!(o._isAd || o.type === 'CLASSIFIED');
  }
  return false;
}

function trendingViews(item: { views?: number | null }): number {
  const v = item.views;
  return typeof v === 'number' && Number.isFinite(v) ? v : 0;
}

export function HomePage({ data }: HomePageProps) {
  const {
    promotedProducts,
    trendingProducts,
    trendingClassifiedAds = [],
    classifiedAds = [],
    boostedClassifiedAds = [],
    categories,
    verifiedStores,
    meta,
  } = data;
  const promoted = promotedProducts ?? [];
  const boostedAds = ((boostedClassifiedAds as ClassifiedAd[])?.length
    ? (boostedClassifiedAds as ClassifiedAd[])
    : (classifiedAds as ClassifiedAd[]).filter((ad) => ad.is_boosted)) ?? [];
  const TRENDING_SECTION_LIMIT = 8;
  const TRENDING_PILL_LIMIT = 4;

  const trendingSorted = useMemo(() => {
    const productItems = (trendingProducts ?? []).map((item) => ({
      kind: 'product' as const,
      item,
      views: trendingViews(item as { views?: number | null }),
    }));
    const adItems = (trendingClassifiedAds ?? []).map((item) => ({
      kind: 'ad' as const,
      item,
      views: trendingViews(item as { views?: number | null }),
    }));
    return [...productItems, ...adItems].sort((a, b) => b.views - a.views);
  }, [trendingProducts, trendingClassifiedAds]);

  const trendingItems = useMemo(
    () => trendingSorted.slice(0, TRENDING_SECTION_LIMIT),
    [trendingSorted],
  );
  const ads = boostedAds;
  const [activeTab, setActiveTab] = useState<'all' | 'store' | 'classified'>('all');
  const { openAuthModal } = useAuth();

  const SPOTLIGHT_PRODUCT_LIMIT = 8;
  const SPOTLIGHT_AD_LIMIT = 7;
  const displayProducts = promoted.slice(0, SPOTLIGHT_PRODUCT_LIMIT);
  const displayAds = ads.slice(0, SPOTLIGHT_AD_LIMIT);
  const allItems = [
    ...displayProducts.map((p) => ({ ...p, _type: 'product' as const })),
    ...displayAds.map((a) => ({ ...a, _type: 'ad' as const, _isAd: true })),
  ];
  const filteredByTab =
    activeTab === 'all'
      ? allItems
      : activeTab === 'store'
        ? allItems.filter((i) => i._type === 'product')
        : allItems.filter((i) => i._type === 'ad');
  const isShowcaseMode = true;

  const TRENDING_PILLS = trendingSorted
    .slice(0, TRENDING_PILL_LIMIT)
    .map(({ item }) => {
      const q = ('title' in item && item.title) || ('name' in item && item.name) || '';
      return { q: String(q), label: String(q) };
    })
    .filter((pill) => pill.q);

  return (
    <div className="mt-3 md:mt-6 flex-1">
      {/* HERO - match React Home.jsx including Trending pills */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-3 mb-5">
        <div className="lg:col-span-8 bg-brand-dark dark:bg-neutral-800 rounded-3xl p-4 md:p-6 text-white relative overflow-hidden shadow-2xl flex flex-col justify-center">
          <div
            className="absolute inset-0 opacity-[0.22] pointer-events-none"
            aria-hidden
            style={{
              backgroundImage: [
                'repeating-linear-gradient(90deg, rgba(255,255,255,0.07) 0, rgba(255,255,255,0.07) 1px, transparent 1px, transparent 22px)',
                'repeating-linear-gradient(0deg, rgba(255,255,255,0.07) 0, rgba(255,255,255,0.07) 1px, transparent 1px, transparent 22px)',
              ].join(', '),
            }}
          />
          <div className="relative z-10 max-w-xl">
            <span className="inline-flex items-center gap-1.5 bg-white/10 backdrop-blur-md text-brand-orange px-2.5 py-0.5 rounded-full text-[9px] md:text-[10px] font-bold mb-2.5 md:mb-3 border border-white/10">
              <span className="w-1.5 h-1.5 rounded-full bg-brand-orange animate-pulse" />
              Fastest Growing Marketplace
            </span>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-black mb-2.5 md:mb-3 leading-tight tracking-tight">
              <span className="text-brand-orange">Buy Smart</span>
              <br />
              <span className="text-white">Sell Smart</span>
            </h1>
            <div className="mb-3 md:mb-4">
              <span className="text-white text-xs md:text-sm mr-2.5">Trending:</span>
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {TRENDING_PILLS.map(({ q, label }) => (
                  <Link
                    key={q}
                    href={`/search?q=${encodeURIComponent(q)}`}
                    className="px-2.5 py-1 bg-neutral-300/30 hover:bg-neutral-300/40 text-white rounded-full text-[11px] md:text-xs font-semibold transition-colors"
                  >
                    {label}
                  </Link>
                ))}
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <Link
                href="/search"
                className="w-full sm:w-auto bg-brand-orange text-white px-4 md:px-5 py-2 rounded-xl font-bold hover:bg-brand-orange-hover transition-all shadow-lg shadow-brand-orange/20 transform hover:-translate-y-1 flex items-center justify-center gap-1.5 text-xs md:text-sm"
              >
                Start Exploring <ChevronRight size={16} />
              </Link>
              <ThemedExternalLink
                href={laravelPaths.postAd()}
                className="w-full sm:w-auto px-4 md:px-5 py-2 rounded-xl font-bold text-white border border-white/20 hover:bg-white/10 transition-all text-xs md:text-sm text-center"
              >
                Post Ad
              </ThemedExternalLink>
            </div>
          </div>
          <div className="absolute -right-20 -bottom-32 w-80 h-80 bg-brand-orange/20 rounded-full blur-3xl opacity-50 md:opacity-100" />
        </div>
        <div className="hidden lg:grid lg:col-span-4 grid-cols-1 gap-3">
          <ThemedExternalLink
            href={laravelPaths.verifiedStores()}
            className="bg-white dark:bg-neutral-800 rounded-3xl p-4 md:p-5 border border-neutral-200 dark:border-neutral-700 flex flex-col justify-center relative overflow-hidden shadow-sm hover:shadow-lg transition-all group cursor-pointer"
          >
            <div className="relative z-10">
              <div className="w-10 h-10 bg-green-50 dark:bg-green-900/20 rounded-2xl flex items-center justify-center mb-2.5 group-hover:scale-110 transition-transform">
                <ShieldCheck size={20} className="text-brand-green" />
              </div>
              <h3 className="text-base md:text-lg font-bold text-brand-dark dark:text-neutral-100 mb-1.5">Official Stores</h3>
              <p className="text-neutral-500 dark:text-neutral-400 text-xs md:text-sm mb-3">Brand new items from verified shops</p>
              <span className="text-brand-green font-bold text-xs md:text-sm flex items-center gap-1.5 group-hover:translate-x-1 transition-transform">
                Browse Shops <ChevronRight size={14} />
              </span>
            </div>
          </ThemedExternalLink>
          <Link
            href={buildListingHref({ listing_type: 'classifieds' })}
            className="bg-white dark:bg-neutral-800 rounded-3xl p-4 md:p-5 border border-neutral-200 dark:border-neutral-700 flex flex-col justify-center relative overflow-hidden shadow-sm hover:shadow-lg transition-all group cursor-pointer"
          >
            <div className="absolute top-0 right-0 p-4 opacity-10">
              <Tag size={64} className="text-brand-orange" />
            </div>
            <div className="relative z-10">
              <div className="w-10 h-10 bg-orange-50 dark:bg-orange-900/30 rounded-2xl flex items-center justify-center mb-2.5 group-hover:scale-110 transition-transform">
                <Tag size={20} className="text-brand-orange fill-brand-orange" />
              </div>
              <h3 className="text-base md:text-lg font-bold text-brand-dark dark:text-neutral-100 mb-1.5">Classified Ads</h3>
              <p className="text-neutral-500 dark:text-neutral-400 text-xs md:text-sm mb-3">Deals from individual sellers</p>
              <span className="text-brand-orange font-bold text-xs md:text-sm flex items-center gap-1.5">
                Browse Ads <ChevronRight size={14} />
              </span>
            </div>
          </Link>
        </div>
      </div>

      {/* Leaderboard banner placement - matches buyme-react home */}
      <div className="mb-5">
        <ThirdPartyAd placement="homepage" />
      </div>

      {/* Product Grid - Spotlight with All / Store / Classified tabs (match React) */}
      <div id="product-grid" className="grid gap-4 md:gap-5">
        <div>
          <div className="mb-3 md:mb-4">
            {isShowcaseMode && (
              <div className="flex items-center gap-2 mb-2.5 md:mb-3">
                <div className="w-5 h-5 bg-brand-orange rounded-lg flex items-center justify-center">
                  <Star size={14} className="text-white fill-white" />
                </div>
                <h2 className="text-base md:text-lg font-black text-brand-dark dark:text-neutral-100 tracking-tight">Spotlight Showcase</h2>
              </div>
            )}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-2.5 md:gap-3">
              <div className="inline-flex bg-neutral-100 dark:bg-neutral-700 p-1 rounded-lg self-start w-full md:w-auto overflow-x-auto">
                <button
                  type="button"
                  onClick={() => setActiveTab('all')}
                  className={`flex-1 md:flex-none px-4 py-2 rounded-lg text-xs uppercase tracking-wide transition-all whitespace-nowrap ${activeTab === 'all' ? 'bg-white dark:bg-neutral-800 text-brand-dark dark:text-neutral-100 font-bold shadow-sm' : 'text-neutral-500 dark:text-neutral-400 font-normal'}`}
                >
                  All
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('store')}
                  className={`flex-1 md:flex-none px-4 py-2 rounded-lg text-xs uppercase tracking-wide transition-all whitespace-nowrap ${activeTab === 'store' ? 'bg-white dark:bg-neutral-800 text-brand-dark dark:text-neutral-100 font-bold shadow-sm' : 'text-neutral-500 dark:text-neutral-400 font-normal'}`}
                >
                  Store
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('classified')}
                  className={`flex-1 md:flex-none px-4 py-2 rounded-lg text-xs uppercase tracking-wide transition-all whitespace-nowrap ${activeTab === 'classified' ? 'bg-white dark:bg-neutral-800 text-brand-dark dark:text-neutral-100 font-bold shadow-sm' : 'text-neutral-500 dark:text-neutral-400 font-normal'}`}
                >
                  Classified
                </button>
              </div>
            </div>
          </div>
          <div className="spotlight-showcase-grid grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2.5 md:gap-4 p-3 md:p-4 rounded-3xl border border-brand-orange/20 dark:border-neutral-700 bg-brand-orange/5 dark:bg-neutral-800">
            {filteredByTab.map((item, i) => {
              if (item._type === 'ad' || isClassifiedAd(item)) {
                return <ClassifiedAdCard key={`ad-${item.id}`} ad={item as ClassifiedAd} />;
              }
              return <ProductCard key={`product-${item.id}`} product={item as ProductSummary} priority={i === 0} />;
            })}
            {isShowcaseMode && filteredByTab.length > 0 && (
              <button
                type="button"
                onClick={() => openAuthModal('register')}
                className="w-full bg-gradient-to-b from-neutral-50 to-white dark:from-transparent dark:to-transparent dark:bg-neutral-800 rounded-2xl border border-neutral-200 dark:border-neutral-700 flex flex-col items-center justify-center p-4 md:p-6 text-center group cursor-pointer min-h-[250px] md:min-h-[380px] hover:border-brand-orange/50 transition-colors relative overflow-hidden col-span-2 md:col-span-1"
              >
                <div className="absolute top-0 left-0 w-full h-1 bg-brand-orange transform -translate-x-full group-hover:translate-x-0 transition-transform duration-500" />
                <div className="w-12 h-12 md:w-14 md:h-14 bg-orange-50 dark:bg-orange-900/30 rounded-full flex items-center justify-center mb-3 md:mb-4 group-hover:scale-110 transition-transform">
                  <Store size={20} className="text-brand-orange md:w-6 md:h-6" />
                </div>
                <h3 className="text-base md:text-lg font-bold text-brand-dark dark:text-neutral-100 mb-1 md:mb-2">Open your store</h3>
                <p className="text-xs md:text-sm text-neutral-500 dark:text-neutral-400 mb-4 md:mb-6 px-2">Start selling to thousands of buyers on BuyMe.lk</p>
                <span className="text-brand-dark dark:text-neutral-100 font-bold text-xs md:text-sm group-hover:text-brand-orange underline decoration-2 underline-offset-4 transition-all">Register now</span>
              </button>
            )}
          </div>
          {isShowcaseMode && filteredByTab.length > 0 && (
            <div className="mt-5 text-center">
              <p className="text-neutral-500 dark:text-neutral-400 text-sm mb-4">Looking for something else?</p>
              <Link href="/search" className="text-brand-orange font-bold hover:underline">
                Browse Categories
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Trending */}
      {trendingItems.length > 0 && (
        <section className="mt-8 md:mt-12 mb-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base md:text-lg font-black text-brand-dark dark:text-neutral-100 flex items-center gap-2">
              <div className="p-1.5 bg-red-50 dark:bg-red-900/30 rounded-full text-red-500">
                <Flame size={14} className="fill-current animate-pulse md:w-4 md:h-4" />
              </div>
              Trending Now
            </h2>
            <span className="text-[9px] md:text-[10px] font-bold text-neutral-400 uppercase tracking-wider hidden md:block">
              Top viewed · last 45 days
            </span>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 md:gap-4">
            {trendingItems.map((entry) =>
              entry.kind === 'ad' ? (
                <ClassifiedAdCard key={`trending-ad-${entry.item.id}`} ad={entry.item} hideBadge />
              ) : (
                <ProductCard key={`trending-product-${entry.item.id}`} product={entry.item} />
              ),
            )}
          </div>
        </section>
      )}

      {/* Verified Stores */}
      {verifiedStores && (verifiedStores as unknown[]).length > 0 && (
        <section className="mt-6 md:mt-8 mb-10 border-t border-neutral-200 dark:border-neutral-700 pt-5 md:pt-6">
          <div className="flex items-center justify-between mb-4 md:mb-5">
            <h2 className="text-base md:text-lg font-black text-brand-dark dark:text-neutral-100 flex items-center gap-2">
              <div className="w-1 h-5 md:h-6 bg-brand-orange rounded-full" />
              Shop from Verified Stores in Sri Lanka
            </h2>
            <ThemedExternalLink
              href={laravelPaths.verifiedStores()}
              className="text-neutral-500 dark:text-neutral-400 text-[10px] md:text-xs font-bold hover:text-brand-orange transition-colors flex items-center gap-1"
            >
              View Directory <ChevronRight size={12} className="md:w-3.5 md:h-3.5" />
            </ThemedExternalLink>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2.5 md:gap-4">
            {(verifiedStores as { id: number; name: string; slug?: string; logo?: string; is_verified?: boolean }[]).map((store) => (
              <ThemedExternalLink
                key={store.id}
                href={laravelPaths.store(String(store.slug ?? store.id))}
                className="bg-white dark:bg-neutral-800 rounded-xl md:rounded-2xl p-4 md:p-6 border border-neutral-200 dark:border-neutral-700 hover:border-brand-orange/30 hover:shadow-lg transition-all cursor-pointer group block"
              >
                <div className="flex items-center gap-3 md:gap-4 mb-3 md:mb-4">
                  {store.logo ? (
                    <div className="relative shrink-0 w-10 h-10 md:w-14 md:h-14 rounded-full overflow-hidden border border-neutral-200 dark:border-neutral-700 group-hover:border-brand-orange transition-colors">
                      <ListingThumbnail
                        src={store.logo}
                        alt={store.name}
                        className="object-cover"
                        sizes="(max-width: 768px) 40px, 56px"
                      />
                    </div>
                  ) : (
                    <div className="w-10 h-10 md:w-14 md:h-14 bg-neutral-100 dark:bg-neutral-700 rounded-full flex items-center justify-center font-bold text-base md:text-lg text-neutral-400 dark:text-neutral-500 group-hover:bg-brand-dark group-hover:text-white transition-colors">
                      {store.name[0]?.toUpperCase() ?? 'S'}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-sm md:text-lg flex items-center gap-1 text-brand-dark dark:text-neutral-100 group-hover:text-brand-orange transition-colors truncate">
                      {store.name}
                      {store.is_verified && <ShieldCheck size={14} className="text-brand-green md:w-[18px] md:h-[18px] flex-shrink-0" />}
                    </h3>
                    <p className="text-[10px] md:text-xs text-neutral-500 dark:text-neutral-400 uppercase font-bold tracking-wide">Official Store</p>
                  </div>
                </div>
                <div className="w-full py-2 md:py-2.5 bg-neutral-50 dark:bg-neutral-700 rounded-lg text-xs md:text-sm font-bold text-neutral-600 dark:text-neutral-300 group-hover:bg-brand-dark group-hover:text-white transition-all text-center">
                  Visit Shop
                </div>
              </ThemedExternalLink>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
